import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Close,
  Send,
  SmartToy,
} from '@mui/icons-material';
import api from '../services/api';
import { brand, text, border } from '../theme/colors';

const starterMessages = [
  {
    role: 'assistant',
    content: 'Hi, I am Eqaya AI. I can help with bids, milestones, project updates, payments, and using the platform.',
  },
];

const EqayaAIChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(starterMessages);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // No floating button — the widget stays hidden until something dispatches
  // the open event (e.g. the dashboard's "Live Chat Support" button).
  useEffect(() => {
    const openChat = () => setOpen(true);
    window.addEventListener('eqaya:open-ai-chat', openChat);
    return () => window.removeEventListener('eqaya:open-ai-chat', openChat);
  }, []);

  const sendMessage = async () => {
    const content = draft.trim();
    if (!content || loading) return;

    const nextMessages = [...messages, { role: 'user', content }];
    setMessages(nextMessages);
    setDraft('');
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/ai/chat', {
        messages: nextMessages.filter(message => message.role !== 'assistant' || message.content !== starterMessages[0].content),
      });
      const reply = response.data?.data?.reply || 'I could not generate a response. Please try again.';
      setMessages([...nextMessages, { role: 'assistant', content: reply }]);
    } catch (err) {
      setError(err.response?.data?.error || 'Eqaya AI is unavailable right now.');
      setMessages(nextMessages);
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return null;
  }

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        right: { xs: 12, md: 24 },
        bottom: { xs: 84, md: 24 },
        width: { xs: 'calc(100vw - 24px)', sm: 380 },
        maxHeight: { xs: '70vh', sm: 560 },
        zIndex: 1300,
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${border.light}`,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          background: brand.gradient,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <SmartToy fontSize="small" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Eqaya AI
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            Project and platform help
          </Typography>
        </Box>
        <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'white' }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ p: 2, overflowY: 'auto', flex: 1, bgcolor: '#fafafa' }}>
        {messages.map((message, index) => {
          const isUser = message.role === 'user';
          return (
            <Box
              key={`${message.role}-${index}`}
              sx={{
                display: 'flex',
                justifyContent: isUser ? 'flex-end' : 'flex-start',
                mb: 1,
              }}
            >
              <Box
                sx={{
                  maxWidth: '82%',
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  bgcolor: isUser ? brand.primary : 'white',
                  color: isUser ? 'white' : text.primary,
                  border: isUser ? 'none' : `1px solid ${border.light}`,
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {message.content}
              </Box>
            </Box>
          );
        })}
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              Thinking...
            </Typography>
          </Box>
        )}
        {error && (
          <Alert severity="warning" sx={{ mt: 1 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
      </Box>

      <Box sx={{ p: 1.5, borderTop: `1px solid ${border.light}`, bgcolor: 'white' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            fullWidth
            value={draft}
            placeholder="Ask Eqaya AI..."
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                sendMessage();
              }
            }}
            disabled={loading}
          />
          <Button
            variant="contained"
            onClick={sendMessage}
            disabled={loading || !draft.trim()}
            aria-label="Send message"
            sx={{ minWidth: 44, px: 1.25, bgcolor: brand.primary, '&:hover': { bgcolor: brand.primaryDark } }}
          >
            <Send fontSize="small" />
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default EqayaAIChatWidget;
